import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useT } from '../i18n';
import { encodeAvatarRegion } from '../utils/imageCompress';
import './AvatarCropper.css';

// ── AVATAR CROPPER ───────────────────────────────────────────────────────────
// Drag to reposition, pinch or scroll to zoom, circular mask — the Instagram /
// TikTok profile-picture editor.
//
// THE MODEL is two numbers and a scale, held in a ref rather than state:
//
//   scale   1 = the image exactly covers the frame (its shorter side fits).
//           Never below 1, so a gap can never open at the edge.
//   offset  pan in FRAME pixels, from centred.
//
// A ref, because a drag updates on every pointer move and React state would
// re-render the tree ~120 times a second for something the browser can do with
// one transform. The transform is written straight to the node; state carries
// only the zoom slider's value, which changes rarely.
//
// EXPORT converts that view transform back into a source-pixel square and hands
// it to encodeAvatarRegion — the same encoder the no-crop path uses, so both
// produce identically sized, identically compressed output.

const MAX_ZOOM = 4;

export default function AvatarCropper({ image, onCancel, onDone }) {
  const { t } = useT();
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const view = useRef({ scale: 1, x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);       // slider mirror of view.current.scale
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Frame size in CSS pixels. Read from the DOM rather than hard-coded: the
  // frame is responsive, and every calculation below is relative to it.
  const frameSize = () => frameRef.current?.clientWidth || 1;

  // The scale at which the image exactly covers the frame.
  const baseScale = useCallback(() => {
    const img = imgRef.current;
    if (!img) return 1;
    return frameSize() / Math.min(img.naturalWidth, img.naturalHeight);
  }, []);

  // Pan is limited so the frame stays fully covered — you can never drag a
  // corner of the mask off the photo and leave a transparent wedge.
  const clamp = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const v = view.current;
    const k = baseScale() * v.scale;
    const maxX = Math.max(0, (img.naturalWidth * k - frameSize()) / 2);
    const maxY = Math.max(0, (img.naturalHeight * k - frameSize()) / 2);
    v.x = Math.min(maxX, Math.max(-maxX, v.x));
    v.y = Math.min(maxY, Math.max(-maxY, v.y));
  }, [baseScale]);

  const paint = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    const v = view.current;
    const k = baseScale() * v.scale;
    img.style.width = `${img.naturalWidth * k}px`;
    img.style.height = `${img.naturalHeight * k}px`;
    img.style.transform = `translate(-50%, -50%) translate(${v.x}px, ${v.y}px)`;
  }, [baseScale]);

  // Re-fit whenever the image or the frame changes size. Starts centred at
  // scale 1, which is exactly the crop the no-editor path would have produced —
  // so accepting immediately gives the old behaviour.
  useEffect(() => {
    view.current = { scale: 1, x: 0, y: 0 };
    setZoom(1);
    paint();
    const ro = new ResizeObserver(() => { clamp(); paint(); });
    if (frameRef.current) ro.observe(frameRef.current);
    return () => ro.disconnect();
  }, [image, paint, clamp]);

  // ── zoom, about the frame's centre ──────────────────────────────────────
  const setScale = useCallback((next) => {
    const v = view.current;
    const clamped = Math.min(MAX_ZOOM, Math.max(1, next));
    // Keep whatever is under the centre of the frame under it after zooming:
    // the offset is in frame pixels, so it scales with the image.
    const ratio = clamped / v.scale;
    v.x *= ratio;
    v.y *= ratio;
    v.scale = clamped;
    clamp();
    paint();
    setZoom(clamped);
  }, [clamp, paint]);

  // ── pointer handling: one finger pans, two pinch ────────────────────────
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  function onPointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        scale: view.current.scale,
      };
    }
  }

  function onPointerMove(e) {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const next = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, next);

    if (pointers.current.size >= 2 && gesture.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (gesture.current.dist > 0) {
        setScale(gesture.current.scale * (dist / gesture.current.dist));
      }
      return;
    }

    // Single pointer: pan by the delta, 1:1 with the finger.
    view.current.x += next.x - prev.x;
    view.current.y += next.y - prev.y;
    clamp();
    paint();
  }

  function onPointerUp(e) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  }

  function onWheel(e) {
    // Trackpads and mice both land here; the sign is all that matters.
    setScale(view.current.scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12));
  }

  // ── export ──────────────────────────────────────────────────────────────
  async function accept() {
    const img = imgRef.current;
    if (!img) return;
    setBusy(true);
    setError('');
    try {
      const v = view.current;
      const k = baseScale() * v.scale;          // source px -> frame px
      const F = frameSize();
      // Top-left of the displayed image in frame coordinates...
      const left = F / 2 - (img.naturalWidth * k) / 2 + v.x;
      const top = F / 2 - (img.naturalHeight * k) / 2 + v.y;
      // ...so the frame's own 0..F maps back to these source pixels.
      const sx = -left / k;
      const sy = -top / k;
      const side = F / k;
      onDone(encodeAvatarRegion(img, sx, sy, side));
    } catch (err) {
      setError(t(err?.message === 'too-large' ? 'ds_err_avatar_large' : 'ds_err_avatar'));
      setBusy(false);
    }
  }

  // Escape closes, matching every other overlay in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="avc-overlay" role="dialog" aria-modal="true">
      <div className="avc">
        <h3 className="avc-title">{t('avc_title')}</h3>

        <div
          className="avc-frame"
          ref={frameRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
        >
          <img ref={imgRef} src={image.src} alt="" className="avc-img" draggable="false" onLoad={paint} />
          {/* The mask is a ring of shadow, not a clip: the parts of the photo
              outside the circle stay visible but dimmed, so you can see what
              you are about to cut off rather than aiming blind. */}
          <div className="avc-mask" />
        </div>

        <p className="avc-hint">{t('avc_hint')}</p>

        <div className="avc-zoom">
          <button type="button" className="avc-zoom-btn" onClick={() => setScale(view.current.scale / 1.2)}
            aria-label="−">−</button>
          <input
            type="range" className="avc-range"
            min="1" max={MAX_ZOOM} step="0.01"
            value={zoom}
            onChange={e => setScale(Number(e.target.value))}
            aria-label={t('avc_zoom')}
          />
          <button type="button" className="avc-zoom-btn" onClick={() => setScale(view.current.scale * 1.2)}
            aria-label="+">+</button>
        </div>

        {error && <p className="avc-err">{error}</p>}

        <div className="avc-actions">
          <button type="button" className="avc-btn" onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </button>
          <button type="button" className="avc-btn avc-btn--primary" onClick={accept} disabled={busy}>
            {busy ? '…' : t('avc_apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
